import { abortableFetcher } from "./abortableFetcher.js";
import { fetcherCache } from "./fetcherCache.js";
const DEFAULT_CACHE_TTL_MS = 5 * 60 * 1e3;
const DEDUPLICATE_MANAGER_TTL_MS = 2 * 1e3;
const createCacheKey = (url, requestInit) => {
  let initString;
  try {
    initString = JSON.stringify(requestInit);
  } catch {
    initString = `request-id-${Date.now()}`;
  }
  return `${url}-${initString}`;
};
const deduplicateManager = {
  ttl: DEDUPLICATE_MANAGER_TTL_MS,
  requests: /* @__PURE__ */ new Map(),
  /**
   * Deduplicates already pending requests by caching them in a Map.
   */
  deduplicate: function(cacheKey, createRequest) {
    this.removeStaleRequests();
    if (this.requests.has(cacheKey)) {
      return this.requests.get(cacheKey).promise;
    }
    const requestPromise = createRequest().finally(() => {
      this.requests.delete(cacheKey);
    });
    this.requests.set(cacheKey, {
      promise: requestPromise,
      timestamp: Date.now()
    });
    return requestPromise;
  },
  removeStaleRequests: function() {
    const now = Date.now();
    this.requests.forEach((value, key) => {
      if (now - value.timestamp > this.ttl) {
        this.requests.delete(key);
      }
    });
  }
};
const cachingFetcher = async (url, initialRequestInit, timeout, cacheEnabled = true, ttl = DEFAULT_CACHE_TTL_MS, abortController = void 0) => {
  if (ttl <= 0) {
    throw new Error("TTL must be a positive number");
  }
  const cacheInterface = await fetcherCache();
  const cacheKey = createCacheKey(url, initialRequestInit);
  const isCacheable = cacheEnabled && !!cacheInterface;
  return deduplicateManager.deduplicate(cacheKey, async () => {
    if (isCacheable) {
      let cachedResponse;
      try {
        cachedResponse = await cacheInterface.getItem(cacheKey);
      } catch (error) {
        console.error(
          "cachingFetcher: Unable to access fetcherCache (getItem)",
          error
        );
      }
      if (cachedResponse && cachedResponse.expiry > Date.now()) {
        return cachedResponse.data;
      }
    }
    try {
      const data = await abortableFetcher(
        url,
        initialRequestInit,
        timeout,
        abortController
      );
      if (isCacheable) {
        try {
          await cacheInterface.setItem(cacheKey, {
            data,
            expiry: Date.now() + ttl
          });
        } catch (error) {
          console.error(
            "cachingFetcher: Unable to access fetcherCache (setItem)",
            error
          );
        }
      }
      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(
          `Failed to fetch data from ${url}: ${error.message}`,
          { cause: error }
        );
      }
      throw new Error(
        `An unknown error occurred while fetching data from ${url}`
      );
    }
  });
};
export {
  DEDUPLICATE_MANAGER_TTL_MS,
  cachingFetcher,
  deduplicateManager
};
